class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Wraps a Rebuy widget mount: tracks loading/ready/empty state and wires the shelf arrows.
// The drawer markup is replaced on every cart change, so all setup lives in connectedCallback.
class CartRecommendations extends HTMLElement {
  static EMPTY_TIMEOUT = 6000;

  connectedCallback() {
    this.nav = this.querySelector('.cart-recs__nav');
    if (!this.querySelector('[data-rebuy-id]')) return;

    this.onScroll = this.updateArrows.bind(this);
    // Product images load after Rebuy renders, so watch the shelf's size rather than checking once.
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());

    this.nav?.querySelectorAll('[data-direction]').forEach((button) => {
      button.addEventListener('click', () => this.scrollShelf(Number(button.dataset.direction)));
    });

    // Rebuy replaces the [data-rebuy-id] mount element when it renders, so watch the whole section.
    this.observer = new MutationObserver(() => this.sync());
    // characterData + class changes catch Rebuy's add-to-cart button states.
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    this.timedOut = false;
    this.timeout = setTimeout(() => {
      this.timedOut = true;
      this.sync();
    }, CartRecommendations.EMPTY_TIMEOUT);

    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.timeout);
    this.resizeObserver?.disconnect();
    this.scroller?.removeEventListener('scroll', this.onScroll);
  }

  sync() {
    const hasProducts = !!this.querySelector('.rebuy-product-block');
    if (hasProducts) {
      this.dataset.state = 'ready';
      this.bindShelf();
      this.syncAddButtons();
    } else {
      this.dataset.state = this.timedOut ? 'empty' : 'loading';
    }
  }

  // Rebuy shows "Adding..." then "Added!" (text is configurable in Rebuy) while .working is set.
  // Compare against the first working text rather than matching words: a change means it's added.
  syncAddButtons() {
    this.querySelectorAll('.rebuy-button').forEach((button) => {
      if (!button.classList.contains('working')) {
        delete button.dataset.recsWorkingText;
        delete button.dataset.recsStatus;
        return;
      }
      const text = button.textContent.trim();
      button.dataset.recsWorkingText ??= text;
      const status = text === button.dataset.recsWorkingText ? 'adding' : 'added';
      if (button.dataset.recsStatus !== status) button.dataset.recsStatus = status;
    });
  }

  // Rebuy can re-render its grid (e.g. on variant change), so rebind when the element changes.
  bindShelf() {
    if (!this.nav) return;
    const scroller = this.querySelector('.rebuy-product-grid');
    if (scroller === this.scroller) return this.updateArrows();

    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.resizeObserver.unobserve(this.scroller);
    }
    this.scroller = scroller;
    if (this.scroller) {
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver.observe(this.scroller);
      // Card widths change scrollWidth without resizing the scroller itself.
      this.scroller.querySelectorAll('.rebuy-product-block').forEach((block) => this.resizeObserver.observe(block));
    }
    this.updateArrows();
  }

  scrollShelf(direction) {
    if (!this.scroller) return;
    // Page by one full view; the gap keeps the next card snapped flush left.
    const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
    this.scroller.scrollBy({ left: direction * (this.scroller.clientWidth + gap) });
  }

  updateArrows() {
    if (!this.nav) return;
    const scroller = this.scroller;
    const overflows = scroller && scroller.scrollWidth - scroller.clientWidth > 1;
    this.nav.hidden = !overflows;
    if (!overflows) return;

    const [prev, next] = this.nav.querySelectorAll('[data-direction]');
    prev.disabled = scroller.scrollLeft <= 1;
    next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  }
}

customElements.define('cart-recommendations', CartRecommendations);

// REBUY-RELATED FUNCTIONS

let didRebuyVariantChange = false;
function handleRebuyVariantChange(event) {
    // Set the variable to true when the event occurs
    didRebuyVariantChange = true;
}
document.addEventListener('rebuy.selectedVariantChange', handleRebuyVariantChange);


var initRebuy = () => { setTimeout(function(){ Rebuy.init(); }, 500); }; document.addEventListener('rebuy:cart.change', function(event){ initRebuy(); });

// Updated for REBUY ADD-ON WIDGET
if (window.__onRebuyAdd) {
  document.removeEventListener('rebuy.add', window.__onRebuyAdd);
}
window.__onRebuyAdd = async function (event) {
  try {
    await updateCartCount();
    await updateCartDrawer();
  } finally {
    const pf = document.querySelector('product-form');
    if (pf) {
      const btn = pf.querySelector('[type="submit"]');
      const spinner = pf.querySelector('.loading__spinner, .loading-overlay__spinner');
      if (btn) {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.add('hidden');
    }

    // open the drawer (if present)
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }

    reattachCartDrawerOverlayClickListener();
  }
};
document.addEventListener('rebuy.add', window.__onRebuyAdd);



// REBUY SELECT DROP-DOWN STYLING
document.addEventListener('DOMContentLoaded', function() {
    // Get all option elements within the .rebuy-select element
const options = document.querySelectorAll('.rebuy-select option');

// Loop through each option element
options.forEach(option => {
    // Set the background color to #000123
    option.style.backgroundColor = '#000123';
    // Set the text color to #fff
    option.style.color = '#fff';
});

});

// Handle overlay click
function reattachCartDrawerOverlayClickListener() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      // Ensure we don't add multiple event listeners
      overlay.removeEventListener('click', cartDrawer.close.bind(cartDrawer));
      overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
    }
  }
}

